import moment from 'moment';
import { getFirstInstallmentSales } from '../../services/firstInstallmentService';

// Constants
export const MONTHS_IN_YEAR = 12;
export const MONTH_DATE_FORMAT = 'YYYY-M';
export const MONTH_DISPLAY_FORMAT = 'MMMM';

export const COLORS = {
  primary: '#1757b8ff',
  primaryStroke: '#8884d8',
  secondary: '#82ca9d',
  white: '#ffffff',
};

export const UI_TEXT = {
  statisticsTitle: 'Estadísticas de Ventas - Primera Cuota',
  yearLabel: 'Año',
  monthLabel: 'Mes',
  updateButtonText: 'Actualizar',
  loadingButtonText: 'Cargando...',
  showDaysWithoutSalesLabel: 'Mostrar dias sin ventas',
  comparePreviousMonthLabel: 'Comparar con mes anterior',
  comparePreviousYearLabel: 'Comparar con año anterior',
  summaryPrefix: 'Resumen:',
  noDataMessage: 'No hay ventas de primera cuota registradas para',
  loadingMessage: 'Cargando estadísticas...',
  yearlyErrorMessage: 'Error al obtener las estadísticas anuales',
  monthlyErrorMessage: 'Error al obtener los datos',
  previousMonthErrorMessage: 'Error al obtener los datos del mes anterior',
};

// Utility Functions
export const getMonthName = (monthIndex) => {
  const monthName = moment().month(monthIndex).format(MONTH_DISPLAY_FORMAT);
  return monthName.charAt(0).toUpperCase() + monthName.slice(1);
};

export const getPreviousPeriod = (year, month) => {
  if (month === 1) {
    return { year: year - 1, month: 12 };
  }
  return { year, month: month - 1 };
};

export const buildDailySalesSeries = (rawData, year, month) => {
  const daysInMonth = moment(`${year}-${month}`, MONTH_DATE_FORMAT).daysInMonth();
  const salesByDay = new Map();
  const originsByDay = new Map();

  rawData.forEach((item) => {
    const day = Number(item.day);
    const count = Number(item.count) || 0;
    if (!Number.isNaN(day)) {
      salesByDay.set(day, (salesByDay.get(day) || 0) + count);
      originsByDay.set(day, [
        ...(originsByDay.get(day) || []),
        ...(item.origins || []),
      ]);
    }
  });

  return Array.from({ length: daysInMonth }, (_, index) => {
    const day = index + 1;
    return {
      day,
      month,
      year,
      count: salesByDay.get(day) || 0,
      origins: originsByDay.get(day) || [],
    };
  });
};

export const buildMonthlySalesSeries = (monthlyDataMap, year) => {
  return Array.from({ length: MONTHS_IN_YEAR }, (_, index) => {
    const monthNum = index + 1;
    const count = monthlyDataMap.get(monthNum) || 0;
    return {
      month: monthNum,
      year,
      count,
    };
  });
};

export const fetchMonthlyDataForYear = async (year) => {
  const { monthlyDataMap } = await fetchYearlySalesData(year);
  return monthlyDataMap;
};

export const calculateTotalSales = (dataMap) => {
  return Array.from(dataMap.values()).reduce((accumulator, current) => accumulator + current, 0);
};

export const formatMonthYearLabel = (month, year) => {
  return `${month}/${year}`;
};

export const getOriginStudentCreatedAt = (origin) => {
  if (!origin) return null;
  return origin.createdAt || origin.studentCreatedAt || origin.student?.createdAt || null;
};

export const isNewCustomerInPeriod = (createdAt, periodYear, periodMonth = null) => {
  if (!createdAt) return false;
  const createdMoment = moment(createdAt);
  if (!createdMoment.isValid()) return false;
  if (periodMonth === null || periodMonth === undefined) {
    return createdMoment.year() === periodYear;
  }
  return createdMoment.year() === periodYear && createdMoment.month() + 1 === periodMonth;
};

export const collectAllOrigins = (dailyData = []) => {
  return (dailyData || []).flatMap((dailyItem) => dailyItem.origins || []);
};

export const countNewCustomers = (dailyData = [], periodYear, periodMonth = null) => {
  return collectAllOrigins(dailyData).filter((originItem) =>
    isNewCustomerInPeriod(getOriginStudentCreatedAt(originItem), periodYear, periodMonth),
  ).length;
};

export const getNewVsUpsellCounts = (dailyData = [], totalSales, periodYear, periodMonth = null) => {
  const newCustomers = countNewCustomers(dailyData, periodYear, periodMonth);
  const resolvedTotal = typeof totalSales === 'number'
    ? totalSales
    : (dailyData || []).reduce((accumulatedSales, dailyItem) => accumulatedSales + (Number(dailyItem.count) || 0), 0);
  return {
    newCustomers,
    upsellCustomers: Math.max(resolvedTotal - newCustomers, 0),
  };
};

export const fetchYearlySalesData = async (targetYear) => {
  const monthRequests = Array.from({ length: MONTHS_IN_YEAR }, (_, index) =>
    getFirstInstallmentSales(targetYear, index + 1)
  );

  const monthResponses = await Promise.all(monthRequests);
  const monthlyDataMap = new Map();
  const allOriginsResponses = [];

  for (let monthNum = 1; monthNum <= MONTHS_IN_YEAR; monthNum++) {
    const response = monthResponses[monthNum - 1];
    if (response.data) {
      const total = response.data.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
      monthlyDataMap.set(monthNum, total);
      allOriginsResponses.push(...response.data);
    }
  }

  return { monthlyDataMap, dailyData: allOriginsResponses };
};

export const getTrimmedGraphicData = (data, startIndex, endIndex) => {
  if (!data.length) return data;
  const clampedStart = Math.max(0, Math.min(startIndex ?? 0, data.length - 1));
  const clampedEnd = Math.max(
    clampedStart,
    Math.min(endIndex ?? data.length - 1, data.length - 1)
  );
  return data.slice(clampedStart, clampedEnd + 1);
};

export const getTrimmedGraphicDataEndIndex = (dataLength, startIndex, endIndex) => {
  if (!dataLength) return 0;
  const clampedStart = Math.max(0, Math.min(startIndex ?? 0, dataLength - 1));
  const clampedEnd = Math.max(
    clampedStart,
    Math.min(endIndex ?? dataLength - 1, dataLength - 1)
  );
  return clampedEnd - clampedStart;
};
